import { DocumentNode, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetMailPreferencesQuery as GetMailPreferences,
} from '__generated__/graphql';

import { Breadcrumbs, Section } from '../../Common/Layout';
import { useNavigation } from '../../Common/useNavigation';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useParticipant } from '../ParticipantProvider';
import EditMailPreferencesMutation from './EditMailPreferencesMutation';
import GetMailPreferencesQuery from './GetMailPreferencesQuery';
import UI from '../../../common/UI';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useMutation from '../../../api/useMutation';
import useProject from '../../../common/useProject';
import useUrlState from '../../../common/useUrlState';

export interface MailPreferencesPageProps {
  participantId: string;
  refetchQueries?: DocumentNode[];
}

const MailPreferencesPage = ({
  participantId, refetchQueries = [GetMailPreferencesQuery],
}: MailPreferencesPageProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { impersonating } = useParticipant();
  const history = useHistory();

  const [{ token }] = useUrlState({
    parse: (urlState, { string }) => ({
      ...urlState,
      token: string(urlState.token),
    }),
  });

  const { data } = useQuery(
    GetMailPreferencesQuery,
    {
      variables: {
        participant_id: participantId,
        token,
      },
      onError: (error) => {
        if (error.graphQLErrors?.length > 0) {
          // Redirect only in case of a normal authentication/validation error.
          history.replace(r('Portal'));
        }
      },
    },
  );

  const participant = data?.participantMailPreferences;

  useDocumentTitle([
    t('email_preferences'),
    'Atleta',
  ]);

  if (!data) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <Breadcrumbs hideHome={impersonating}>
        <UI.Link to={r('Account')}>
          {t('account')}
        </UI.Link>

        <UI.Strong>{t('email_preferences')}</UI.Strong>
      </Breadcrumbs>

      <MailPreferencesForm participant={participant} token={token} refetchQueries={refetchQueries} />
    </>
  );
};

interface MailPreferencesFormProps {
  participant: GetMailPreferences['participantMailPreferences']
  token: string;
  refetchQueries?: DocumentNode[];
}

const MailPreferencesForm = ({ participant, token, refetchQueries }: MailPreferencesFormProps) => {
  const project = useProject();
  const notifier = useNotifier();
  const { t } = useTranslation();

  const [editMailPreferences] = useMutation(
    EditMailPreferencesMutation,
    {
      onCompleted: () => {
        notifier.success(t('information_saved'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const grantedMailPermissionIds = participant.granted_mail_permissions.map(
    (permission) => permission.id,
  );

  const handleEditMailPreferences = (id: string) => {
    const shouldOptIn = !grantedMailPermissionIds.includes(id);
    const mailOptIns = shouldOptIn ? [id] : [];
    const mailOptOuts = !shouldOptIn ? [id] : [];

    editMailPreferences({
      variables: {
        input: {
          participant_id: participant.id,
          mail_opt_ins: mailOptIns,
          mail_opt_outs: mailOptOuts,
          token,
        },
      },
      optimisticResponse: ({
        editMailPreferences: {
          id: participant.id,
          email: participant.email,
          granted_mail_permissions: shouldOptIn
            ? [...participant.granted_mail_permissions, { id, __typename: 'MailPermission' }]
            : participant.granted_mail_permissions.filter((mailPermission) => mailPermission.id !== id),
          available_mail_permissions: participant.available_mail_permissions,
          __typename: 'ParticipantMailPreferences',
        },
      }),
    });
  };

  return (
    <Section
      title={t('manage_email_preferences')}
      intro={(
        <UI.Delimit>
          {project.organisation_name}
          {participant.email}
        </UI.Delimit>
      )}
      spacing="md"
    >
      <UI.FormGrid sc={{ gutter: 0.75 }}>
        {participant.available_mail_permissions.map((mailPermission, index) => (
          <UI.GridContainer
            sc={{ columns: '1fr 48px', alignVertical: 'center' }}
            key={mailPermission.id}
          >
            <UI.Div sc={{ muted: !grantedMailPermissionIds.includes(mailPermission.id) }}>
              <UI.InputLabel sc={{ mb: 0 }}>
                {mailPermission.title}
              </UI.InputLabel>
            </UI.Div>
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Toggle
                checked={grantedMailPermissionIds.includes(mailPermission.id)}
                onChange={() => handleEditMailPreferences(mailPermission.id)}
                name={`mail_permissions.${index}`}
                aria-label={t(grantedMailPermissionIds.includes(mailPermission.id) ? 'disable_name' : 'enable_name', {
                  name: mailPermission.title,
                })}
              />
            </UI.Div>
          </UI.GridContainer>
        ))}
        {participant.available_mail_permissions.length === 0 && t('no_results')}
      </UI.FormGrid>
    </Section>
  );
};

export default MailPreferencesPage;
